import * as types from "../action-types";
import getters from "./getters";
import actions from "./actions";
import moment from "moment";
import { keys, get, set, cloneDeep, extend } from "lodash";

const parseSettings = (device) => {
  const type = device.type;
  let kindFirmwareSettingsKey = `${type}_firmware_settings`;
  let firmwareSettings = get(
    device,
    kindFirmwareSettingsKey,
    device.firmware_settings
  );
  return {
    type,
    backend_settings: device.backend_settings,
    client_device_settings: device.client_device_settings,
    firmware_settings: firmwareSettings,
    [kindFirmwareSettingsKey]: firmwareSettings,
  };
};

/**
 * Determines if AI Active Warnings should be enabled (read-only toggle state)
 *
 * @param {Object} device - The device configuration object
 * @returns {boolean} - True if Active Warnings should be enabled, false otherwise
 */
function isAIActiveWarningsEnabled(device) {
  // Extract settings with safe defaults (assume false/disabled if missing)
  const backendSettings = device?.backend_settings || {};
  const firmwareSettings = device?.firmware_settings;
  const videoSettings = firmwareSettings?.video_settings || {};
  const conciergeSettings = firmwareSettings?.concierge_settings || {};
  const sheilaSettings = firmwareSettings?.sheila_settings || {};
  const paidFeatures = backendSettings.paid_features || {};

  // Require firmware_settings to exist
  if (!firmwareSettings) {
    return false;
  }

  // Happy path: Both consent and motion warnings must be explicitly enabled
  const hasUserConsent = backendSettings.ai_automated_warnings_enabled === true;
  const hasMotionWarningsEnabled =
    backendSettings.motion_warning_enabled === true;

  // Blocking conditions: Any of these being true will disable the feature
  const isE2EEEnabled = videoSettings.encryption_enabled === true;
  const isAlexaPlusEnabled = conciergeSettings.mode === "alexa_plus";
  const isLocalStorageEnabled = sheilaSettings.local_storage_enabled === true;
  const lacksEntitlement = paidFeatures.ai_automated_warnings !== true;

  // Feature is enabled only if:
  // 1. Happy path conditions are met (both must be explicitly true) AND
  // 2. None of the blocking conditions are true
  return (
    hasUserConsent &&
    hasMotionWarningsEnabled &&
    !isE2EEEnabled &&
    !isAlexaPlusEnabled &&
    !isLocalStorageEnabled &&
    !lacksEntitlement
  );
}

const parseKeepAlive = (state, device) => {
  const status = Boolean(device["connected"]);
  const timestamp = device["last_keep_alive"];
  const hostname = device["hostname"];
  state.device = Object.assign({}, state.device, {
    connected: status,
    last_keep_alive: { timestamp, hostname },
  });
};

const initialState = () =>
  cloneDeep({
    notes: [],
    tags: [],
    device: { connected: undefined },
    settings: {},
    reports: [],
    lastReport: {},
    motionSchedules: [],
    weekDaysAbbr: {
      sunday: "Su",
      monday: "Mo",
      tuesday: "Tu",
      wednesday: "We",
      thursday: "Th",
      friday: "Fr",
      saturday: "Sa",
    },
  });
const mutations = {
  [types.FETCH_NOTES.SUCCESS](state, notes) {
    state.notes = notes || [];
  },
  [types.FETCH_NOTES.ERROR](state, error) {
    state.notes = [];
  },
  [types.FETCH_TAGS.SUCCESS](state, tags) {
    state.tags = tags || [];
  },
  [types.FETCH_TAGS.ERROR](state, error) {
    state.tags = [];
  },
  [types.FETCH_DEVICE.SUCCESS](state, { device, profile }) {
    if (device) {
      let settings = parseSettings(device);
      state.device.type = state.device.type || state.device.kind;
      state.device.kind = state.device.type;
      state.device = extend({}, device, settings);
      if (device.hasOwnProperty("connection_status")) {
        parseKeepAlive(state, device.connection_status);
      }
      settings.nd = {
        nd_ai_active_warnings_enabled: isAIActiveWarningsEnabled(device),
      };
      state.settings = settings;
      console.info("isAIActiveWarningsEnabled", state.settings);
    }
    if (profile) {
      //state.device.profile = profile;
      state.device.retailer = profile.retailer;
    }
  },
  [types.FETCH_DEVICE.ERROR](state, device) {
    state.settings = {};
  },
  [types.UPDATE_DEVICE_SETTINGS.SUCCESS](state, device) {
    device.type = state.device.type || state.device.kind;
    device.kind = state.device.type;
    let settings = parseSettings(device);
    let dev = extend({}, state.device, device);
    state.device = extend({}, dev, settings);
    state.settings = settings;
  },
  [types.UPDATE_DEVICE_PROFILE](state, profile) {
    state.device.profile = profile;
  },
  [types.UPDATE_DEVICE_SETTINGS.ERROR](state) {
    //reset values
    state.settings = parseSettings(state.device);
  },
  [types.FETCH_MOTION_SCHEDULE_SETTING.SUCCESS](state, motionSchedules) {
    const formatTimeRange = ({
      start_hour,
      start_minute,
      end_hour,
      end_minute,
    }) => {
      const createTime = (hours, minutes) => {
        return moment().utc().hours(hours).minutes(minutes).format("HH:mm");
      };
      return {
        from: createTime(start_hour, start_minute),
        to: createTime(end_hour, end_minute),
      };
    };
    state.motionSchedules = motionSchedules.map((sch) => {
      // DAYS
      const dayNames = keys(sch.days);
      const activeDays = dayNames.filter((name) => sch.days[name]);
      activeDays.sort((a, b) =>
        Object.keys(state.weekDaysAbbr).indexOf(a) <
        Object.keys(state.weekDaysAbbr).indexOf(b)
          ? -1
          : 1
      );
      const abbreviated = activeDays.map((name) => state.weekDaysAbbr[name]);
      sch.days = abbreviated.join("/");

      // TIME FROM/TO
      sch.time_range = formatTimeRange(sch.time_range);
      return sch;
    });
  },
  [types.FETCH_MOTION_SCHEDULE_SETTING.ERROR](state) {
    state.motionSchedules = [];
  },
  [types.FETCH_LAST_STATUS_REPORT.SUCCESS](state, report) {
    state.lastReport = report || {};
  },
  [types.FETCH_STATUS_REPORT.SUCCESS](state, report) {
    state.reports = get(report, "reports", []);
  },
  [types.FETCH_STATUS_REPORT.ERROR](state) {
    state.reports = [];
  },
  [types.FETCH_LAST_STATUS_REPORT.ERROR](state) {
    state.lastReport = {};
  },
  [types.SET_DEVICE_STATUS.SUCCESS](state, deviceStatus) {
    parseKeepAlive(state, deviceStatus);
  },
  [types.SET_DEVICE_STATUS.ERROR](state) {
    state.device = Object.assign({}, state.device, { connected: null });
  },
  [types.RESET_DEVICE_STATUS](state) {
    state.device = Object.assign({}, state.device, { connected: undefined });
  },
  resetState(state) {
    Object.assign(state, initialState());
  },
  updateDeviceState(state, request) {
    if (state.device.profile) {
      if (request.type === "CustomerRequest::Replacement") {
        state.device.profile.state = "Replace process";
        set(state.device, "last_requests.replacement", request);
      }
      if (request.type === "CustomerRequest::Refund") {
        state.device.profile.state = "Refund process";
        set(state.device, "last_requests.refund", request);
      }
    }
  },
  updateDeviceZoneSettings(state, zoneSettings) {
    set(state.device, "firmware_settings.zone_settings", zoneSettings);
  },
};

export default {
  state: initialState(),
  mutations,
  actions,
  getters,
  namespaced: true,
};
